/**
 * Aggregate InventoryMovement ledger into per-product purchased/sold totals
 * for Reports merchandising grid (not a timeline — use queryInventoryMovementHistory for that).
 *
 * Type sets use string literals so this module can load during stockMovementLedger
 * circular graph (instantCache → … → ledger).
 */

import { listStockMovements } from './stockMovementLedger';

const INBOUND_TYPES = new Set([
  'PURCHASE',
  'SALE_RETURN',
  'PRODUCTION_OUTPUT',
  'TRANSFER_IN'
]);

const OUTBOUND_TYPES = new Set([
  'SALE',
  'CONSUMPTION',
  'PRODUCTION_CONSUME',
  'EXPIRY',
  'TRANSFER_OUT'
]);

/**
 * @param {object} parsed — { stockMovements, stockMovementsArchive? }
 * @returns {Map<string, { purchased: number, sold: number, net: number }>}
 */
export function aggregateMovementProductStats(parsed = {}) {
  const byProduct = new Map();
  const list = listStockMovements(parsed, { includeArchive: true });

  for (const m of list) {
    if (!m || m.productId == null || m.productId === '') continue;
    const key = String(m.productId);
    let row = byProduct.get(key);
    if (!row) {
      row = { purchased: 0, sold: 0, net: 0 };
      byProduct.set(key, row);
    }

    const qty = Number(m.quantity) || 0;
    if (!qty) continue;
    const type = String(m.movementType || '').toUpperCase();
    row.net += qty;

    if (qty > 0) {
      if (INBOUND_TYPES.has(type) || type === 'ADJUSTMENT' || type === 'VOID') {
        row.purchased += qty;
      }
    } else if (qty < 0) {
      if (OUTBOUND_TYPES.has(type) || type === 'ADJUSTMENT' || type === 'VOID') {
        row.sold += Math.abs(qty);
      }
    }
  }

  return byProduct;
}

/**
 * Resolve purchased/sold for one product id from ledger map (string + number keys).
 * @returns {{ purchased: number, sold: number, net: number }|null}
 */
export function getMovementProductStats(map, productId) {
  if (!map || productId == null) return null;
  const a = map.get(String(productId));
  if (a) return a;
  const n = Number(productId);
  if (!Number.isNaN(n) && map.get(String(n))) return map.get(String(n));
  return null;
}
